import { Dispatch } from 'redux';
import { queryClient } from 'src/query-client';
import {
  AssetVersionCommentDto,
  SetAssetFavoriteDto,
  AssetItemDto,
  AssetVersionCommentUserDto,
  AssetVersionItemDto,
  GetAssetVersionCommentsSuccessDto
} from '@api/Api';
import { setFavoriteAsset } from '@api/Asset';
import {
  AssetsSliderAnchor,
  CopiedCommentsData,
  MediaViewerActionTypes,
  IMediaViewerAction
} from '@redux/types/mediaViewerType';
import {
  optimisticArrayItemAdd,
  optimisticArrayItemRemove,
  optimisticArrayItemUpdate
} from '@hooks/queries/helpers';
import type {
  SetAssetCommentLikeParams,
  SetAssetCommentResolveParams
} from '@layouts/types';
import { QUERY_COMMENTS_ASSETS } from '@hooks/queries/asset';
import { storeAssetsSliderOpen } from '@helpers/assetsSliderOpenStorage';

export const setAsset = (asset: AssetItemDto | null) => ({
  type: MediaViewerActionTypes.SET_ASSET,
  payload: asset
});

export const setAssetVariant = (id: string | null) => ({
  type: MediaViewerActionTypes.SET_SELECTED_VERSION,
  payload: { id }
});

// COMMENTS
const commentsListQuery = [QUERY_COMMENTS_ASSETS];

const commentsLens = {
  get: (data: GetAssetVersionCommentsSuccessDto) => data.comments,
  set: (
    data: GetAssetVersionCommentsSuccessDto,
    comments: AssetVersionCommentDto[]
  ): GetAssetVersionCommentsSuccessDto => ({ ...data, comments })
};

export const createNewComment = async (
  newComment: AssetVersionCommentDto,
  assetVersionId: string
) => {
  await queryClient.cancelQueries({ queryKey: commentsListQuery });
  optimisticArrayItemAdd<
    AssetVersionCommentDto,
    GetAssetVersionCommentsSuccessDto
  >(
    {
      queryKey: [...commentsListQuery, assetVersionId],
      lens: commentsLens
    },
    newComment
  );
};

export const editComment = async (comment: AssetVersionCommentDto) => {
  await queryClient.cancelQueries({ queryKey: commentsListQuery });
  optimisticArrayItemUpdate<
    AssetVersionCommentDto,
    GetAssetVersionCommentsSuccessDto
  >(
    { queryKey: commentsListQuery, id: comment.id, lens: commentsLens },
    (item) => {
      return {
        ...item,
        ...comment
      };
    }
  );
};

export const setCommentLike = async (
  data: SetAssetCommentLikeParams,
  user: AssetVersionCommentUserDto
) => {
  await queryClient.cancelQueries({ queryKey: commentsListQuery });
  optimisticArrayItemUpdate<
    AssetVersionCommentDto,
    GetAssetVersionCommentsSuccessDto
  >(
    { queryKey: commentsListQuery, id: data.id, lens: commentsLens },
    (item) => ({
      ...item,
      likes: data.value
        ? [
            ...item.likes,
            {
              ...user,
              me: true
            }
          ]
        : item.likes.filter((el) => el.id !== user.id)
    })
  );
};

export const setCommentResolve = async (
  data: SetAssetCommentResolveParams,
  user: AssetVersionCommentUserDto
) => {
  await queryClient.cancelQueries({ queryKey: commentsListQuery });
  optimisticArrayItemUpdate<
    AssetVersionCommentDto,
    GetAssetVersionCommentsSuccessDto
  >(
    { queryKey: commentsListQuery, id: data.id, lens: commentsLens },
    (item) => ({
      ...item,
      resolvedAt: data.value ? new Date().toISOString() : null,
      resolvedBy: data.value ? user : null
    })
  );
};

export const removeComment = async (id: string) => {
  await queryClient.cancelQueries({ queryKey: commentsListQuery });
  optimisticArrayItemRemove<
    AssetVersionCommentDto,
    GetAssetVersionCommentsSuccessDto
  >({
    queryKey: commentsListQuery,
    id,
    lens: commentsLens
  });
};

export const removeCommentAttachment = async (data: {
  commentId: string;
  attachmentId: string;
}) => {
  const { commentId, attachmentId } = data;
  await queryClient.cancelQueries({ queryKey: commentsListQuery });
  optimisticArrayItemUpdate<
    AssetVersionCommentDto,
    GetAssetVersionCommentsSuccessDto
  >(
    { queryKey: commentsListQuery, id: commentId, lens: commentsLens },
    (item) => {
      return {
        ...item,
        attachments: item.attachments.filter(
          (attachment) => attachment.id !== attachmentId
        )
      };
    }
  );
};

export const updateAssetSelectedVersion = (data: {
  newSelectedVersion: AssetVersionItemDto;
}) => ({
  type: MediaViewerActionTypes.UPDATE_SELECTED_VERSION,
  payload: data
});

export const toggleFavoriteAssetInMV =
  (params: SetAssetFavoriteDto) =>
  async (dispatch: Dispatch<IMediaViewerAction>) => {
    await setFavoriteAsset(params);

    dispatch({
      type: MediaViewerActionTypes.TOGGLE_ASSET_FAVORITE,
      payload: { isFavorite: params.isFavorite }
    });
  };

export const setToggleOpenTranscript = (isOpenTranscript: boolean | null) => ({
  type: MediaViewerActionTypes.SET_TOGGLE_OPEN_TRANSCRIPT,
  payload: { isOpenTranscript }
});

export const setAssetsSliderOpen = (isAssetsSliderOpen: boolean) => {
  storeAssetsSliderOpen(isAssetsSliderOpen);
  return {
    type: MediaViewerActionTypes.SET_ASSETS_SLIDER_OPEN,
    payload: isAssetsSliderOpen
  };
};

export const setAssetsSliderAnchor = (
  assetsSliderAnchor: AssetsSliderAnchor | null
) => ({
  type: MediaViewerActionTypes.SET_ASSETS_SLIDER_ANCHOR,
  payload: assetsSliderAnchor
});

export const setCopiedComments = (
  copiedComments: CopiedCommentsData | null
) => ({
  type: MediaViewerActionTypes.SET_COPIED_COMMENTS,
  payload: copiedComments
});
